import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { userMaterialGetAllFolderItems } from "@/services/user-material-get-all-folder-items";
import { userMaterialUpdateAgentUserMaterialFolder } from "@/services/user-material-user-agent-update-folder";
import { useTranslation } from "react-i18next";

type MaterialType = "" | "video" | "podcast" | "moments" | "post" | "article";

type FolderItem = {
  id: number;
  folderName: string;
  createTime: number;
  updateTime?: number;
  userId?: number;
  folderId?: number;
  agentId?: number;
  itemCount?: number;
  type?: number;
};

type ChangeEventDetail = { folders: FolderItem[] };
type ChangeEvent = CustomEvent<ChangeEventDetail>;

class AgentUserMaterialsEvents {
  constructor(private eventName: string) { }
  listen(listener: (e: ChangeEvent) => void): () => void {
    window.addEventListener(this.eventName, listener as EventListener);
    return () => {
      window.removeEventListener(this.eventName, listener as EventListener);
    };
  }
  dispatch(detail: ChangeEventDetail): void {
    const event = new CustomEvent<ChangeEventDetail>(this.eventName, { detail });
    window.dispatchEvent(event);
  }
}

class AgentUserMaterialsLocal {
  constructor(private storageKey: string) { }
  async get(): Promise<FolderItem[] | null> {
    return await jsonStore.get<FolderItem[]>(this.storageKey);
  }
  async set(folders: FolderItem[]): Promise<void> {
    await jsonStore.set(this.storageKey, folders);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AgentUserMaterialsRemote {
  constructor(private cache: AgentUserMaterialsLocal, private agentId?: string, private type?: MaterialType) { }
  async getCached() {
    return await this.cache.get();
  }
  async fetch(): Promise<FolderItem[]> {
    const data = await userMaterialGetAllFolderItems({ agentId: this.agentId || "", type: this.type || "" });
    const folders = data?.folderList.map(item => ({
      id: item.id,
      folderName: item.folderName,
      createTime: item.createTime,
      updateTime: item.updateTime,
      userId: item.userId,
      folderId: item.folderId,
      agentId: item.agentId,
      itemCount: item.itemCount,
      type: item.type,
      latestImages: item.latestImages?.map(image => image.itemUrl),
    }));
    await this.cache.set(folders);
    return folders;
  }
  async updateFolders(folderIds: number[]) {
    return await userMaterialUpdateAgentUserMaterialFolder({
      folderIds,
      agentId: this.agentId || "",
      type: this.type || "",
    });
  }
}

export class AgentUserMaterialsManager {
  private readonly STORAGE_PREFIX = "MIUTA_AGENT_USER_MATERIALS";
  private folders: FolderItem[] = [];
  private cache: AgentUserMaterialsLocal;
  private api: AgentUserMaterialsRemote;
  private events: AgentUserMaterialsEvents;

  constructor(agentId?: string, type?: MaterialType) {
    const key = `${this.STORAGE_PREFIX}:${agentId || "all"}:${type || "all"}`;
    this.cache = new AgentUserMaterialsLocal(key);
    this.api = new AgentUserMaterialsRemote(this.cache, agentId, type);
    this.events = new AgentUserMaterialsEvents(key);
  }

  getFolders(): FolderItem[] {
    return this.folders;
  }

  private setFolders(folders: FolderItem[]) {
    this.folders = folders;
    this.dispatch();
  }

  async init(): Promise<void> {
    try {
      const cached = await this.api.getCached();
      if (cached) {
        this.setFolders(cached);
        this.fetch();
        return;
      }
    } catch (error) {
      console.error("Agent user materials fetch failed:", error.message);
      throw error;
    }
    await this.fetch();
  }

  async fetch(): Promise<void> {
    const folders = await this.api.fetch();
    this.setFolders(folders);
  }

  async updateFolders(folderIds: number[]) {
    try {
      await this.api.updateFolders(folderIds);
      await this.fetch();
    } catch (error) {
      console.error("Agent user materials update failed:", error.message);
      throw error;
    }
  }

  reset(): void {
    this.setFolders([]);
    this.clear();
  }

  clear(): void {
    this.cache.clear();
  }

  listen(listener: (e: ChangeEvent) => void): () => void {
    return this.events.listen(listener);
  }

  private dispatch(): void {
    this.events.dispatch({ folders: this.folders });
  }
}

export const agentUserMaterialsHooks = {
  useFolderListData(props: { agentId?: string; type?: MaterialType }) {
    const { agentId, type } = props;
    const { showToastByCode, toast } = useToast();
    const { t } = useTranslation();

    const managerRef = useRef<AgentUserMaterialsManager | null>(null);

    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
      managerRef.current = new AgentUserMaterialsManager(agentId, type);
      setFolders(managerRef.current.getFolders());
      const unsubscribe = managerRef.current.listen((e) => setFolders(e.detail.folders));
      return unsubscribe;
    }, [agentId, type]);

    useEffect(() => {
      const run = async () => {
        if (!managerRef.current) return;
        setLoading(true);
        try {
          await managerRef.current.init();
        } catch (error) {
          showToastByCode(error);
        } finally {
          setLoading(false);
        }
      };
      run();
    }, [agentId, type]);

    const refresh = async () => {
      if (!managerRef.current) return;
      setLoading(true);
      try {
        await managerRef.current.fetch();
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    // 更新
    const updateMaterials = async (folderIds: number[], isAdd?: boolean) => {
      // 已有的情况：只允许添加一个文件夹
      if (folders.length >= 1 && isAdd) {
        toast.default({
          description: t('agent_creation.subscription_settings.add_remove_modal.disabled_tips'),
        });
        return false;
      }

      // 创建多个文件夹的情况：只允许添加一个文件夹，取第一个
      if (folderIds.length > 1 && isAdd) {
        folderIds = [folderIds[0]];
        toast.default({
          description: t('agent_creation.subscription_settings.add_remove_modal.disabled_tips'),
        });
      }

      setUpdateLoading(true);
      try {
        await managerRef.current.updateFolders(folderIds);
        return true
      } catch (error) {
        showToastByCode(error);
        return false;
      } finally {
        setUpdateLoading(false);
      }
    };

    return { folders, loading, refresh, updateMaterials, updateLoading };
  },
};

export default AgentUserMaterialsManager;
